import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { registerUser } from '../../services/auth';
import { useAuth } from '../../context/AuthContext';

// Stili modernizzati in linea con il componente Login
const RegisterContainer = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 40px;
  background-color: rgba(44, 18, 73, 0.8);
  border-radius: 24px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3), 
    0 0 0 1px rgba(255, 255, 255, 0.1),
    inset 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  
  @media (min-width: 992px) {
    max-width: 520px;
    padding: 45px;
  }
`;

const Title = styled.h2`
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
  font-size: 2.4rem;
  color: #ff3eb5;
  margin-bottom: 30px;
  text-align: center;
  text-shadow: 0 2px 15px rgba(255, 62, 181, 0.4);
  position: relative;
  
  &::after {
    content: "";
    display: block;
    width: 60px;
    height: 3px;
    background: #ff3eb5;
    margin: 8px auto 0;
    border-radius: 3px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`;

const Label = styled.label`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  font-weight: 500;
  margin-left: 4px;
  transition: all 0.3s ease;
  text-align: left;
  display: block;
`;

const Input = styled.input`
  padding: 16px 20px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.15);
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    border-color: #ff3eb5;
    background-color: rgba(0, 0, 0, 0.25);
    box-shadow: 
      0 0 0 3px rgba(255, 62, 181, 0.2),
      inset 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  &:hover:not(:focus) {
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 18px;
  background: #ffaa00;
  color: #ffffff;
  border: none;
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 15px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(255, 170, 0, 0.3);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 25px rgba(255, 170, 0, 0.4);
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 5px 15px rgba(255, 170, 0, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 8px 20px rgba(255, 170, 0, 0.2);
  }
`;

const ErrorMessage = styled.div`
  color: #ff5555;
  background-color: rgba(255, 85, 85, 0.1);
  padding: 15px;
  border-radius: 16px;
  font-size: 0.95rem;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 85, 85, 0.2);
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: "!";
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    background-color: rgba(255, 85, 85, 0.9);
    color: white;
    border-radius: 50%;
    font-weight: bold;
  }
`;

const LinkContainer = styled.div`
  margin-top: 25px;
  text-align: center;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);

  a {
    color: #00d9ff;
    text-decoration: none;
    font-weight: 500;
    padding: 2px 6px;
    border-radius: 4px;
    transition: all 0.3s ease;
    position: relative;
    
    &::after {
      content: "";
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 1px;
      background: #00d9ff;
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.3s ease;
    }
    
    &:hover {
      color: #38ffd8;
      background-color: rgba(0, 217, 255, 0.1);
      
      &::after {
        transform: scaleX(1);
      }
    }
  }
`;

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { loginContext } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    try {
      const registeredUser = await registerUser(email, password, name);
      if (registeredUser) {
        await loginContext();
        navigate('/dashboard');
      } else {
        setError('Registration did not return user data. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      let errorMessage = 'Failed to create account. Please try again.';
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegisterContainer>
      <Title>Create an Account</Title>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Full Name</Label>
          <Input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
            placeholder="John Doe"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            placeholder="your@email.com"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            placeholder="••••••••"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <Input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            placeholder="••••••••"
            required
          />
        </FormGroup>
        
        <Button type="submit" disabled={loading}>
          {loading ? 'Creating Account...' : 'Sign Up'}
        </Button>
      </Form>
      
      <LinkContainer>
        Already have an account? <Link to="/login">Log in</Link>
      </LinkContainer>
    </RegisterContainer>
  );
};

export default Register; 